import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchUsers, fetchUser, updateUser, deleteUser, createAuthUser, updateAuthUser, resendVerification,
  requestPasswordReset,
  fetchAvailabilitySchedule,
  fetchDaysOffs, createDaysOff, updateDaysOff, deleteDaysOff,
  fetchPartners, fetchPartner,
} from "api";
import { PageHeader, Table, Spinner, EmptyState, Modal, FormField, Select } from "components/common";
import { AvailabilityDisplay, AvailabilityModal, ALL_DAYS } from "components/availability";
import { useAuth } from "hooks/useAuth";
import type { ContactUser, PartnerEntity, Role, DayOfWeek, AvailabilitySchedule, DaysOff, DaysOffStatus, AuthUserInfo } from "types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

type StatusFilter = "all" | "active" | "inactive";
type RoleFilter   = "all" | "bridge13_admin" | "bridge13_courier" | "partner" | "none";

const ROLE_LABELS: Record<string, string> = {
  bridge13_admin:   "Admin",
  bridge13_courier: "Courier",
  partner:          "Partner",
};

function formatRole(role?: string | null): string {
  if (!role) return "—";
  return ROLE_LABELS[role] ?? role;
}

function fuzzyMatch(text: string, query: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  if (lower.includes(q)) return true;
  let ti = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const found = lower.indexOf(q[qi], ti);
    if (found === -1) return false;
    ti = found + 1;
  }
  return true;
}

// ─── Users list ───────────────────────────────────────────────────────────────

export function UsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [roleFilter, setRoleFilter]     = useState<RoleFilter>("all");
  const [roleTarget, setRoleTarget]     = useState<ContactUser | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn:  () => fetchUsers(),
  });

  const filtered = users.filter((u) => {
    // Status filter
    if (statusFilter === "active" && !u.active) return false;
    if (statusFilter === "inactive" && u.active) return false;

    // Role filter
    if (roleFilter === "none" && u.role) return false;
    if (roleFilter !== "all" && roleFilter !== "none" && u.role !== roleFilter) return false;

    // Search
    if (search.trim()) {
      const fullName = `${u.name_1}${u.name_2 ? ` ${u.name_2}` : ""}`;
      if (!fuzzyMatch(fullName, search.trim())) return false;
    }

    return true;
  });

  if (isLoading) return <Spinner className="mt-20" />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader
        title="Users"
        subtitle={`${filtered.length} of ${users.length} users`}
        action={
          <button className="btn-primary" onClick={() => navigate("/users/new")}>
            Add user
          </button>
        }
      />

      {/* Search + filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text"
          className="input flex-1 min-w-[200px]"
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Status filter */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
          {(["all", "active", "inactive"] as StatusFilter[]).map((value) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                statusFilter === value
                  ? "bg-gray-800 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {value}
            </button>
          ))}
        </div>

        {/* Role filter */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
          {([
            { value: "all",              label: "All roles" },
            { value: "bridge13_admin",   label: "Admin" },
            { value: "bridge13_courier", label: "Courier" },
            { value: "partner",          label: "Partner" },
            { value: "none",             label: "No role" },
          ] as { value: RoleFilter; label: string }[]).map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setRoleFilter(value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                roleFilter === value
                  ? "bg-gray-800 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <EmptyState message={search || statusFilter !== "all" || roleFilter !== "all" ? "No users match your filters." : "No users yet."} />
        ) : (
          <Table<ContactUser>
            data={filtered}
            keyExtractor={(u) => u.id}
            onRowClick={(u) => navigate(`/users/${u.id}`)}
            columns={[
              {
                header: "Name",
                accessor: (u) => `${u.name_1}${u.name_2 ? ` ${u.name_2}` : ""}`,
              },
              { header: "Email", accessor: (u) => u.email ?? "—" },
              {
                header: "Role",
                accessor: (u) => (
                  <span className={`badge ${
                    u.role === "bridge13_admin"   ? "bg-purple-100 text-purple-700" :
                    u.role === "bridge13_courier" ? "bg-blue-100 text-blue-700" :
                    u.role === "partner"          ? "bg-amber-100 text-amber-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {formatRole(u.role)}
                  </span>
                ),
              },
              {
                header: "Verified",
                accessor: (u) => {
                  if (u.verified === null || u.verified === undefined) return <span className="text-gray-300">—</span>;
                  return (
                    <span className={`badge ${u.verified ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {u.verified ? "Verified" : "Pending"}
                    </span>
                  );
                },
              },
              {
                header: "Status",
                accessor: (u) => (
                  <span className={`badge ${u.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {u.active ? "Active" : "Inactive"}
                  </span>
                ),
              },
              {
                header: "",
                accessor: (u) => (
                  <UserActionsMenu
                    onAssignRole={() => setRoleTarget(u)}
                  />
                ),
                className: "w-10",
              },
            ]}
          />
        )}
      </div>

      {roleTarget && (
        <AssignRoleModal
          user={roleTarget}
          onClose={() => setRoleTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            setRoleTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ─── User Actions Menu ────────────────────────────────────────────────────────

function UserActionsMenu({ onAssignRole }: { onAssignRole: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-400 hover:text-gray-600"
        title="Actions"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-8 z-50 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
          <button
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); onAssignRole(); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Assign role
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Assign Role Modal ────────────────────────────────────────────────────────

const ASSIGNABLE_ROLES: { value: Role; label: string; color: string }[] = [
  { value: "bridge13_admin",   label: "Admin",   color: "bg-purple-100 text-purple-700" },
  { value: "bridge13_courier", label: "Courier", color: "bg-blue-100 text-blue-700" },
  { value: "partner",          label: "Partner", color: "bg-amber-100 text-amber-700" },
];

function AssignRoleModal({
  user,
  onClose,
  onSuccess,
}: {
  user:      ContactUser;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const hasAuthUser = !!user.role;

  const { mutate, isPending, error } = useMutation({
    mutationFn: (role: Role) => {
      if (hasAuthUser) {
        // User already has an auth_user — update the role
        // We need the auth_user id; since we don't have it directly,
        // we use the createAuthUser endpoint which handles upsert-like behavior.
        // Actually the API has separate create/update — we need a different approach.
        // For users with existing roles, we update via the auth_users endpoint.
        return updateAuthUser(user.id, role);
      }
      // No auth_user yet — create one
      return createAuthUser(user.id, user.email ?? "", role);
    },
    onSuccess,
  });

  const fullName = `${user.name_1}${user.name_2 ? ` ${user.name_2}` : ""}`;

  return (
    <Modal title={`Assign role — ${fullName}`} onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">
        {hasAuthUser
          ? `Currently assigned: ${formatRole(user.role)}. Select a new role below.`
          : "This user has no login credentials. Selecting a role will create auth credentials."}
      </p>
      <div className="space-y-1">
        {ASSIGNABLE_ROLES.map(({ value, label, color }) => (
          <button
            key={value}
            onClick={() => mutate(value)}
            disabled={isPending}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors hover:bg-gray-50 flex items-center justify-between ${
              user.role === value ? "bg-gray-50" : ""
            }`}
          >
            <span className="flex items-center gap-2">
              <span className={`badge ${color}`}>{label}</span>
            </span>
            {user.role === value && (
              <span className="text-xs text-gray-400">Current</span>
            )}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-500 mt-3">Something went wrong. Please try again.</p>}
    </Modal>
  );
}

// ─── Verified Cell ────────────────────────────────────────────────────────────

function VerifiedCell({ userId, verified }: { userId: number; verified: boolean }) {
  const queryClient = useQueryClient();
  const [sent, setSent] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: () => resendVerification(userId),
    onSuccess: () => {
      setSent(true);
      queryClient.invalidateQueries({ queryKey: ["users", userId] });
    },
  });

  if (verified) {
    return <span className="badge bg-green-100 text-green-700">Verified</span>;
  }

  return (
    <span className="flex items-center gap-2">
      <span className="badge bg-yellow-100 text-yellow-700">Pending invitation</span>
      <button
        onClick={() => mutate()}
        disabled={isPending || sent}
        className="text-xs text-brand-600 hover:underline disabled:opacity-50 disabled:no-underline"
      >
        {isPending ? "Sending…" : sent ? "Sent!" : "Resend email"}
      </button>
    </span>
  );
}

// ─── User detail ──────────────────────────────────────────────────────────────

export function UserDetailPage() {
  const { id }       = useParams<{ id: string }>();
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const { isAdmin }  = useAuth();
  const userId       = Number(id);
  const [showEditModal,      setShowEditModal]       = useState(false);
  const [showScheduleModal,  setShowScheduleModal]  = useState(false);
  const [showDaysOffModal,   setShowDaysOffModal]    = useState(false);
  const [showAuthModal,      setShowAuthModal]       = useState(false);
  const [showDeleteModal,    setShowDeleteModal]     = useState(false);

  const { data: user, isLoading } = useQuery({
    queryKey: ["users", userId],
    queryFn:  () => fetchUser(userId),
  });

  const isCourierUser = user?.role === "bridge13_courier";

  const { data: availSchedule, isLoading: loadingSchedule } = useQuery({
    queryKey: ["availabilitySchedule", userId],
    queryFn:  () => fetchAvailabilitySchedule(userId),
    enabled:  !!user && isCourierUser,
  });

  const { data: daysOffs = [], isLoading: loadingDaysOffs } = useQuery({
    queryKey: ["daysOffs", userId],
    queryFn:  () => fetchDaysOffs(userId),
    enabled:  !!userId,
  });

  const { data: partner } = useQuery({
    queryKey: ["partners", user?.partner_entity_id],
    queryFn:  () => fetchPartner(user!.partner_entity_id),
    enabled:  isAdmin && !!user?.partner_entity_id,
  });

  const { mutate: toggleActive, isPending } = useMutation({
    mutationFn: (active: boolean) => updateUser(userId, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users", userId] }),
  });

  if (isLoading) return <Spinner className="mt-20" />;
  if (!user)     return <EmptyState message="User not found." />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-2xl space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
      >
        ← Back
      </button>

      <PageHeader
        title={`${user.name_1}${user.name_2 ? ` ${user.name_2}` : ""}`}
        subtitle={user.title ?? undefined}
      />

      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">User details</h2>
          {isAdmin && (
            <button className="btn-secondary text-xs py-1" onClick={() => setShowEditModal(true)}>
              Edit
            </button>
          )}
        </div>
        <Row label="Email" value={user.email ?? "—"} />
        <Row label="Phone" value={user.phone ?? "—"} />
        <Row label="Title" value={user.title ?? "—"} />
        <Row
          label="Status"
          value={
            <span className={`badge ${user.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {user.active ? "Active" : "Inactive"}
            </span>
          }
        />
      </div>

      {/* ── Auth user (admin only) ────────────────────────────────────────── */}
      {isAdmin && (
        <AuthUserSection
          user={user}
          showModal={showAuthModal}
          onOpenModal={() => setShowAuthModal(true)}
          onCloseModal={() => setShowAuthModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["users", userId] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
            setShowAuthModal(false);
          }}
        />
      )}

      {/* ── Partner (admin only) ───────────────────────────────────────────── */}
      {isAdmin && (
        <div className="card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Partner</h2>
          {partner ? (
            <Table<PartnerEntity>
              data={[partner]}
              keyExtractor={(p) => p.id}
              onRowClick={(p) => navigate(`/partners/${p.id}`)}
              columns={[
                { header: "Name",   accessor: (p) => p.name },
                {
                  header: "Type",
                  accessor: (p) => (
                    <span className={`badge capitalize ${
                      p.type === "donator"     ? "bg-green-100 text-green-700" :
                      p.type === "distributor" ? "bg-indigo-100 text-indigo-700" :
                      p.type === "ops"         ? "bg-orange-100 text-orange-700" :
                      "bg-gray-100 text-gray-500"
                    }`}>
                      {p.type}
                    </span>
                  ),
                },
                {
                  header: "Status",
                  accessor: (p) => (
                    <span className={`badge ${p.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  ),
                },
              ]}
            />
          ) : (
            <p className="text-sm text-gray-400">No partner assigned.</p>
          )}
        </div>
      )}

      {/* ── Availability Schedule (couriers only) ──────────────────────────── */}
      {isCourierUser && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">Availability schedule</h2>
            <button
              className="btn-secondary text-xs py-1"
              onClick={() => setShowScheduleModal(true)}
            >
              {availSchedule ? "Edit schedule" : "Add schedule"}
            </button>
          </div>

          {loadingSchedule ? (
            <Spinner className="py-4" />
          ) : availSchedule ? (
            <AvailabilityDisplay availability={availSchedule.availability} />
          ) : (
            <p className="text-sm text-gray-400">No availability schedule set.</p>
          )}
        </div>
      )}

      {/* ── Days off ───────────────────────────────────────────────────────── */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700">Time away</h2>
          <button
            className="btn-secondary text-xs py-1"
            onClick={() => setShowDaysOffModal(true)}
          >
            Request time away
          </button>
        </div>

        {loadingDaysOffs ? (
          <Spinner className="py-4" />
        ) : daysOffs.length === 0 ? (
          <p className="text-sm text-gray-400">No days off requested.</p>
        ) : (
          <div className="space-y-2">
            {daysOffs.map((d) => (
              <DaysOffRow
                key={d.id}
                daysOff={d}
                isAdmin={isAdmin}
                onStatusChange={() => queryClient.invalidateQueries({ queryKey: ["daysOffs", userId] })}
                onDelete={() => queryClient.invalidateQueries({ queryKey: ["daysOffs", userId] })}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Danger zone ─────────────────────────────────────────────────── */}
      {isAdmin && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Danger zone</h2>
          <button
            className="btn-primary bg-red-600 hover:bg-red-700"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete
          </button>
        </div>
      )}

      {/* ── Edit User Modal ────────────────────────────────────────────────── */}
      {showEditModal && (
        <EditUserModal
          user={user}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["users", userId] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
            setShowEditModal(false);
          }}
        />
      )}

      {/* ── Availability Schedule Modal ────────────────────────────────────── */}
      {showScheduleModal && (
        <AvailabilityModal
          userId={userId}
          existing={availSchedule ?? null}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["availabilitySchedule", userId] });
            setShowScheduleModal(false);
          }}
        />
      )}

      {/* ── Days Off Modal ─────────────────────────────────────────────────── */}
      {showDaysOffModal && (
        <RequestDaysOffModal
          userId={userId}
          onClose={() => setShowDaysOffModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["daysOffs", userId] });
            setShowDaysOffModal(false);
          }}
        />
      )}

      {/* ── Delete User Modal ───────────────────────────────────────────────── */}
      {showDeleteModal && (
        <DeleteUserModal
          user={user}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            navigate("/users");
          }}
        />
      )}
    </div>
  );
}

// ─── Auth User Section ────────────────────────────────────────────────────────

function AuthUserSection({
  user,
  showModal,
  onOpenModal,
  onCloseModal,
  onSuccess,
}: {
  user:         ContactUser;
  showModal:    boolean;
  onOpenModal:  () => void;
  onCloseModal: () => void;
  onSuccess:    () => void;
}) {
  const authInfo: AuthUserInfo | null = user.auth_user_info ?? null;
  const [resendSent,  setResendSent]  = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const { mutate: resend, isPending: resending } = useMutation({
    mutationFn: () =>
      user.verified
        ? requestPasswordReset(authInfo!.email)
        : resendVerification(user.id),
    onSuccess: () => { setResendSent(true); setResendError(null); },
    onError:   () => setResendError("Failed to send email. Please try again."),
  });

  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">Login access</h2>
        {authInfo && (
          <button className="btn-secondary text-xs py-1" onClick={onOpenModal}>
            Edit
          </button>
        )}
      </div>

      {authInfo ? (
        <>
          <Row label="Auth ID"     value={String(authInfo.id)} />
          <Row label="Login email" value={authInfo.email} />
          <Row
            label="Role"
            value={
              <span className={`badge ${
                authInfo.role === "bridge13_admin"   ? "bg-purple-100 text-purple-700" :
                authInfo.role === "bridge13_courier" ? "bg-blue-100 text-blue-700"   :
                authInfo.role === "partner"          ? "bg-amber-100 text-amber-700" :
                "bg-gray-100 text-gray-500"
              }`}>
                {formatRole(authInfo.role)}
              </span>
            }
          />
          <Row
            label="Verified"
            value={
              <span className={`badge ${user.verified ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                {user.verified ? "Verified" : "Pending"}
              </span>
            }
          />

          <div className="pt-1 space-y-1">
            <button
              className="btn-secondary text-xs py-1"
              disabled={resending || resendSent}
              onClick={() => resend()}
            >
              {resending ? "Sending…" : resendSent ? "Email sent!" : "Resend auth email"}
            </button>
            {resendError && <p className="text-xs text-red-500">{resendError}</p>}
          </div>
        </>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-400">No login access assigned.</p>
          <button className="btn-secondary text-xs py-1" onClick={onOpenModal}>
            Assign login
          </button>
        </div>
      )}

      {showModal && (
        <AssignRoleModal
          user={user}
          onClose={onCloseModal}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="text-sm text-gray-500 w-32 shrink-0">{label}</span>
      <span className="text-sm text-gray-900">{value}</span>
    </div>
  );
}

// ─── Days Off Row ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<DaysOffStatus, string> = {
  requested: "bg-amber-100 text-amber-700",
  approved:  "bg-green-100 text-green-700",
  rejected:  "bg-red-100 text-red-600",
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month:   "short",
    day:     "numeric",
    year:    "numeric",
    hour:    "numeric",
    minute:  "2-digit",
  });
}

// Converts an ISO string to the "YYYY-MM-DDTHH:MM" format required by datetime-local inputs
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function DaysOffRow({
  daysOff,
  isAdmin,
  onStatusChange,
  onDelete,
}: {
  daysOff:        DaysOff;
  isAdmin:        boolean;
  onStatusChange: () => void;
  onDelete:       () => void;
}) {
  const [editing,       setEditing]       = useState(false);
  const [editStartAt,   setEditStartAt]   = useState("");
  const [editEndAt,     setEditEndAt]     = useState("");
  const [editStatus,    setEditStatus]    = useState<DaysOffStatus>(daysOff.status);

  const { mutate: save, isPending: saving, error: saveError } = useMutation({
    mutationFn: () =>
      updateDaysOff(daysOff.id, {
        start_at: new Date(editStartAt).toISOString(),
        end_at:   new Date(editEndAt).toISOString(),
        status:   editStatus,
      }),
    onSuccess: () => { setEditing(false); onStatusChange(); },
  });

  const { mutate: changeStatus, isPending: changingStatus } = useMutation({
    mutationFn: (status: DaysOffStatus) => updateDaysOff(daysOff.id, { status }),
    onSuccess: onStatusChange,
  });

  const { mutate: remove, isPending: deleting } = useMutation({
    mutationFn: () => deleteDaysOff(daysOff.id),
    onSuccess: onDelete,
  });

  const openEdit = () => {
    setEditStartAt(toDatetimeLocal(daysOff.start_at));
    setEditEndAt(toDatetimeLocal(daysOff.end_at));
    setEditStatus(daysOff.status);
    setEditing(true);
  };

  const endBeforeStart = editStartAt && editEndAt && new Date(editEndAt) <= new Date(editStartAt);

  if (editing) {
    return (
      <div className="bg-gray-50 rounded-lg px-4 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label text-xs">Start</label>
            <input
              type="datetime-local"
              className="input text-sm"
              value={editStartAt}
              onChange={(e) => setEditStartAt(e.target.value)}
            />
          </div>
          <div>
            <label className="label text-xs">End</label>
            <input
              type="datetime-local"
              className="input text-sm"
              value={editEndAt}
              onChange={(e) => setEditEndAt(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="label text-xs">Status</label>
          <Select
            size="sm"
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value as DaysOffStatus)}
          >
            <option value="requested">Requested</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Select>
        </div>

        {endBeforeStart && (
          <p className="text-xs text-red-500">End must be after start.</p>
        )}
        {saveError && (
          <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            className="btn-primary text-xs py-1.5 px-3"
            disabled={saving || !!endBeforeStart || !editStartAt || !editEndAt}
            onClick={() => save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            className="btn-secondary text-xs py-1.5 px-3"
            disabled={saving}
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 bg-gray-50 rounded-lg px-4 py-3">
      <div className="space-y-0.5 text-sm">
        <div className="text-gray-800 font-medium">
          {formatDateTime(daysOff.start_at)} – {formatDateTime(daysOff.end_at)}
        </div>
        <span className={`badge text-xs ${STATUS_STYLES[daysOff.status]}`}>
          {daysOff.status.charAt(0).toUpperCase() + daysOff.status.slice(1)}
        </span>
      </div>

      {isAdmin && (
        <div className="flex items-center gap-1 shrink-0">
          {daysOff.status === "requested" && (
            <>
              <button
                className="text-xs px-2 py-1 rounded-lg border border-green-200 text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50"
                disabled={changingStatus}
                onClick={() => changeStatus("approved")}
              >
                Approve
              </button>
              <button
                className="text-xs px-2 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                disabled={changingStatus}
                onClick={() => changeStatus("rejected")}
              >
                Reject
              </button>
            </>
          )}
          <button
            className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
            onClick={openEdit}
            title="Edit"
          >
            Edit
          </button>
          <button
            className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
            disabled={deleting}
            onClick={() => remove()}
            title="Delete"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Request Days Off Modal ───────────────────────────────────────────────────

function RequestDaysOffModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId:    number;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const [startAt, setStartAt] = useState("");
  const [endAt,   setEndAt]   = useState("");

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      createDaysOff({
        contact_user_id: userId,
        start_at:        new Date(startAt).toISOString(),
        end_at:          new Date(endAt).toISOString(),
      }),
    onSuccess,
  });

  const endBeforeStart = startAt && endAt && new Date(endAt) <= new Date(startAt);
  const isValid = startAt && endAt && !endBeforeStart;

  return (
    <Modal title="Request time away" onClose={onClose}>
      <div className="space-y-4">
        <FormField label="Start">
          <input
            type="datetime-local"
            className="input"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </FormField>
        <FormField label="End">
          <input
            type="datetime-local"
            className="input"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
          />
        </FormField>
        {endBeforeStart && (
          <p className="text-xs text-red-500">End must be after start.</p>
        )}
        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}
        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Submit request"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Delete User Modal ────────────────────────────────────────────────────────

function DeleteUserModal({
  user,
  onClose,
  onSuccess,
}: {
  user:      ContactUser;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const name = `${user.name_1}${user.name_2 ? ` ${user.name_2}` : ""}`;

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess,
  });

  return (
    <Modal title="Delete user" onClose={onClose}>
      <p className="text-sm text-gray-600 mb-4">
        Are you sure you want to remove <strong>{name}</strong>?
      </p>
      <div className="flex gap-3">
        <button className="btn-secondary" onClick={onClose}>No</button>
        <button
          className="btn-primary bg-red-600 hover:bg-red-700"
          disabled={isPending}
          onClick={() => mutate()}
        >
          {isPending ? <Spinner className="h-4" /> : "Yes"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────

const ROLE_OPTIONS: { value: Role | ""; label: string }[] = [
  { value: "",                 label: "No role" },
  { value: "bridge13_admin",   label: "Admin" },
  { value: "bridge13_courier", label: "Courier" },
  { value: "partner",          label: "Partner" },
];

function EditUserModal({
  user,
  onClose,
  onSuccess,
}: {
  user:      ContactUser;
  onClose:   () => void;
  onSuccess: () => void;
}) {
  const [name1,     setName1]     = useState(user.name_1 ?? "");
  const [name2,     setName2]     = useState(user.name_2 ?? "");
  const [email,     setEmail]     = useState(user.email ?? "");
  const [phone,     setPhone]     = useState(user.phone ?? "");
  const [title,     setTitle]     = useState(user.title ?? "");
  const [role,      setRole]      = useState<Role | "">((user.role ?? "") as Role | "");
  const [partnerId, setPartnerId] = useState<string>(String(user.partner_entity_id ?? ""));
  const [active,    setActive]    = useState(user.active);

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      // Update contact user fields
      await updateUser(user.id, {
        name_1:            name1.trim() || undefined,
        name_2:            name2.trim() || undefined,
        email:             email.trim() || undefined,
        phone:             phone.trim() || undefined,
        title:             title.trim() || undefined,
        partner_entity_id: partnerId ? Number(partnerId) : undefined,
        active,
      });

      // Update role if changed
      const newRole = role || null;
      const oldRole = user.role ?? null;
      if (newRole !== oldRole) {
        if (newRole) {
          if (oldRole) {
            await updateAuthUser(user.id, newRole);
          } else {
            await createAuthUser(user.id, (email.trim() || user.email) ?? "", newRole);
          }
        }
        // Note: removing a role (newRole = null) is not supported by the current API
      }
    },
    onSuccess,
  });

  const isValid = name1.trim().length > 0;

  return (
    <Modal title="Edit user" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField label="First name">
            <input
              type="text"
              className="input"
              value={name1}
              onChange={(e) => setName1(e.target.value)}
            />
          </FormField>
          <FormField label="Last name">
            <input
              type="text"
              className="input"
              value={name2}
              onChange={(e) => setName2(e.target.value)}
            />
          </FormField>
        </div>

        <FormField label="Email">
          <input
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>

        <FormField label="Phone">
          <input
            type="tel"
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </FormField>

        <FormField label="Title">
          <input
            type="text"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </FormField>

        <FormField label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value as Role | "")}>
            {ROLE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Partner">
          <Select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
            <option value="">— Select partner —</option>
            {partners.map((p: PartnerEntity) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Status">
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setActive(true)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                active
                  ? "border-green-300 bg-green-50 text-green-700"
                  : "border-gray-200 text-gray-500 hover:border-green-300 hover:text-green-600"
              }`}
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setActive(false)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                !active
                  ? "border-gray-300 bg-gray-100 text-gray-700"
                  : "border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700"
              }`}
            >
              Inactive
            </button>
          </div>
        </FormField>

        {error && <p className="text-sm text-red-500">Something went wrong. Please try again.</p>}

        <div className="flex gap-3 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!isValid || isPending}
            onClick={() => mutate()}
          >
            {isPending ? <Spinner className="h-4" /> : "Save changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

